import type { OutpatientClinicTaskTypeGroup } from '@common/contexts/support/domain/models/outpatientClinicTaskTypeGroupModel';
import type { OutpatientClinicTaskType } from '@common/contexts/support/domain/models/outpatientClinicTaskTypeModel';
import { fetchTaskTypes } from '../services/fetchTaskTypes';

type TaskTypeEpisodeCategory = {
    groups: TaskTypeGroupAndTypes[];
    id: OutpatientClinicTaskType['episodeCategory']['id'];
    name: OutpatientClinicTaskType['episodeCategory']['name'];
};

type TaskTypeGroupAndTypes = {
    id: OutpatientClinicTaskTypeGroup['id'];
    name: OutpatientClinicTaskTypeGroup['name'];
    sortOrder: OutpatientClinicTaskTypeGroup['sortOrder'];
    types: {
        id: OutpatientClinicTaskType['id'];
        name: OutpatientClinicTaskType['name'];
        sortOrder: OutpatientClinicTaskType['sortOrder'];
        withDescription: OutpatientClinicTaskType['withDescription'];
    }[];
};

type EpisodeCategory = {
    id?: string;
    name?: string;
};

type GetTaskGroupsAndTypesOptions = {
    abortSignal: AbortSignal;
    episodeCategory: EpisodeCategory | EpisodeCategory[];
};

export async function getTaskGroupsAndTypesForEpisodeCategory(
    episodeCategory: EpisodeCategory,
    abortSignal: AbortSignal
) {
    const options: GetTaskGroupsAndTypesOptions = {
        abortSignal,
        episodeCategory,
    };

    const taskGroupsAndTypes = await getTaskGroupsAndTypes(options);

    return taskGroupsAndTypes.find((entry) => entry.id === episodeCategory.id) ?? null;
}

export async function getTaskGroupsAndTypes(options: GetTaskGroupsAndTypesOptions) {
    const taskTypes = await fetchTaskTypes(options);

    const episodeCategoryGroupsAndTypes = new Map<string, TaskTypeEpisodeCategory>();

    taskTypes.forEach((taskType) => {
        const episodeCategory = (() => {
            const found = episodeCategoryGroupsAndTypes.get(taskType.episodeCategory.id);

            if (found) {
                return found;
            }

            const newEpisodeCategory = {
                groups: [],
                id: taskType.episodeCategory.id,
                name: taskType.episodeCategory.name,
            };

            episodeCategoryGroupsAndTypes.set(taskType.episodeCategory.id, newEpisodeCategory);

            return newEpisodeCategory;
        })();

        const group = (() => {
            const found = episodeCategory.groups.find((entry) => entry.id === taskType.group.id);

            if (found) {
                return found;
            }

            const newGroup = {
                id: taskType.group.id,
                name: taskType.group.name,
                sortOrder: taskType.group.sortOrder,
                types: [],
            };

            episodeCategory.groups.push(newGroup);

            return newGroup;
        })();

        group.types.push({
            id: taskType.id,
            name: taskType.name,
            sortOrder: taskType.sortOrder,
            withDescription: taskType.withDescription,
        });
    });

    episodeCategoryGroupsAndTypes.forEach((episodeCategoryEntry) => {
        episodeCategoryEntry.groups.forEach((entry) => {
            entry.types.sort((a, b) => a.sortOrder - b.sortOrder);
        });

        episodeCategoryEntry.groups.sort((a, b) => a.sortOrder - b.sortOrder);
    });

    return Array.from(episodeCategoryGroupsAndTypes.values());
}
